import { FixMeLater } from "types";
import { displayResultMessage, openMessage, useIdle } from "utils/misc";
import AntForm from "antd/es/form";
import RouterLink from "common/Buttons/RouterLink";
import React, { useEffect, useRef, useState } from "react";
import Form from "common/Form/Form";
import Input from "common/Inputs/Input";
import {
  LoginRequest,
  LoginResponse,
  LoginResponseWithUserId,
  OAuthUserRequest,
} from "@shared/types";
import { MOBILE_SEARCH } from "common/constants";
import { PrimaryTitle } from "features/auth/styles";
import useMedia from "utils/hooks/useMedia";
import { EMAIL_FORM_ITEM_RULES } from "features/auth/constants";
import AuthFormFooter from "features/auth/AuthFormFooter";
import useQuery from "utils/hooks/useQuery";
import { sharedLib } from "@shared/services/shared.service";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-use";
import { handleUserTempOAuthData } from "@shared/features/onboarding/utils";
import SocialLogins from "features/auth/SocialLogins";
import moment, { MomentInput } from "moment";
import { getUtmUserUuid } from "@shared/utils/misc";

interface LoginFormProps {
  onSuccessfulLogin: (args: LoginRequest | OAuthUserRequest) => void;
  setLoginResponse: (response: LoginResponse) => void;
}

const MAX_NUM_CAPTCHA_REFETCH = 3;

const LoginForm = ({ onSuccessfulLogin, setLoginResponse }: LoginFormProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [form] = AntForm.useForm();
  const [captchaInProgress, setCaptchaInProgress] = useState(false);
  const [login, loginStatus] =
    sharedLib.sharedApi.authentication.useLoginMutation();
  const {
    data: captchaData,
    isFetching: isFetchingGetCaptcha,
    isError: getCaptchaError,
    refetch: refetchGetCaptcha,
  } = sharedLib.sharedApi.authentication.useGetCaptchaQuery();
  const [
    validateCaptcha,
    { isFetching: isLoadingValidateCaptcha, data: validateCaptchaData },
  ] = sharedLib.sharedApi.authentication.useLazyValidateCaptchaQuery();
  const [oauthUser, oauthUserStatus] =
    sharedLib.sharedApi.authentication.useOauthUserMutation();
  const history = useHistory();
  const location = useLocation();
  const lastMessageDisplayedRef = useRef<MomentInput>();
  const [captchaObj, setCaptchaObj] = useState<FixMeLater>();
  const [numCaptchaRefetch, setNumCaptchaRefetch] = useState(0);
  const idle = useIdle(1000 * 60 * 2);
  const query = useQuery();
  const connCode = query?.get("conn_code");
  const oauthPlatform = query?.get("oauth_platform");

  const isLoadingLogin = loginStatus.isLoading || oauthUserStatus.isLoading;
  const isSuccess = loginStatus.isSuccess || oauthUserStatus.isSuccess;
  const originalArgs = loginStatus.originalArgs || oauthUserStatus.originalArgs;

  const loginErrorMessage = "Login failed, please try again later";
  const captchaErrorMessage =
    "Something went wrong when validating captcha information, please try again";
  const maxRefetchErrorMessage =
    "Something went wrong when fetching the captcha information. Please refresh the page and try again";

  const resetAndRefetchCaptcha = () => {
    if (captchaObj) {
      captchaObj.reset();
      setCaptchaObj(undefined);
    }
    if (numCaptchaRefetch >= MAX_NUM_CAPTCHA_REFETCH) {
      if (
        !lastMessageDisplayedRef.current ||
        moment().diff(lastMessageDisplayedRef.current, "seconds") > 5
      ) {
        openMessage(maxRefetchErrorMessage, "error");
        lastMessageDisplayedRef.current = moment();
      }
    } else {
      setNumCaptchaRefetch(numCaptchaRefetch + 1);
      refetchGetCaptcha();
    }
  };

  useEffect(() => {
    if (!idle) {
      resetAndRefetchCaptcha();
    }
  }, [idle]);

  const onError = (message?: string) => {
    if (message) {
      openMessage(captchaErrorMessage, "error");
    }
    setCaptchaInProgress(false);
    resetAndRefetchCaptcha();
  };

  useEffect(() => {
    if (!isFetchingGetCaptcha && captchaData) {
      (window as FixMeLater).initGeetest(
        {
          gt: captchaData.gt,
          challenge: captchaData.challenge,
          offline: !captchaData.success,
          product: "bind",
          new_captcha: true,
          https: true,
          lang: "en",
        },
        (_captchaObj: FixMeLater) => {
          _captchaObj.onReady(() => {
            setCaptchaObj(_captchaObj);
          });
          _captchaObj.onSuccess(() => {
            const result = _captchaObj.getValidate();
            if (result) {
              validateCaptcha({
                challenge: result.geetest_challenge,
                validate: result.geetest_validate,
                seccode: result.geetest_seccode,
              });
            } else {
              onError(captchaErrorMessage);
            }
          });
          _captchaObj.onClose(function () {
            setCaptchaInProgress(false);
          });
          _captchaObj.onError(() => {
            onError(undefined);
          });
        }
      );
    }
  }, [captchaData, isFetchingGetCaptcha]);

  useEffect(() => {
    if (captchaInProgress && captchaObj) {
      captchaObj.verify();
    }
  }, [captchaInProgress, captchaObj]);

  const handleLogin = async () => {
    const utmUuid = await getUtmUserUuid?.();
    if (!connCode) {
      if (validateCaptchaData?.success && validateCaptchaData?.captcha_token) {
        const loginRequest: LoginRequest = {
          ...form.getFieldsValue(),
          captcha_token: validateCaptchaData.captcha_token,
          extra_validations: false,
          utm_user_uuid: utmUuid,
        };
        displayResultMessage(
          login(loginRequest).unwrap(),
          undefined,
          loginErrorMessage
        )
          .then((response: LoginResponse) => {
            if (!response.token) {
              setLoginResponse(response);
            }
          })
          .catch(() => {});
        setCaptchaInProgress(false);
      } else {
        onError(captchaErrorMessage);
      }
    } else if (
      oauthPlatform &&
      connCode &&
      validateCaptchaData?.captcha_token
    ) {
      displayResultMessage(
        oauthUser({
          code: connCode,
          captcha_token: validateCaptchaData.captcha_token,
          oauth_platform: parseInt(oauthPlatform),
          utm_user_uuid: utmUuid,
        }).unwrap(),
        undefined,
        loginErrorMessage
      )
        .then(async (result: LoginResponseWithUserId) => {
          if (!result.token) {
            setLoginResponse(result);
          }
          history.replace(location.pathname || "");
          if (result?.user_id) {
            await handleUserTempOAuthData(result.user_id);
          }
        })
        .catch(() => {});
    }
  };

  useEffect(() => {
    if (validateCaptchaData) {
      handleLogin();
    }
  }, [validateCaptchaData]);

  useEffect(() => {
    if (isSuccess && originalArgs) {
      onSuccessfulLogin(originalArgs);
    }
  }, [isSuccess]);

  const isLoading =
    isLoadingLogin ||
    isLoadingValidateCaptcha ||
    (captchaInProgress && isFetchingGetCaptcha);

  const onFinish = () => {
    if (getCaptchaError) {
      resetAndRefetchCaptcha();
    }
    if (!captchaInProgress) {
      setCaptchaInProgress(true);
    }
  };

  useEffect(() => {
    if (connCode && oauthPlatform) {
      onFinish();
    }
  }, [connCode, oauthPlatform]);

  return (
    <Form
      name="login"
      form={form}
      initialValues={{
        remember: true,
      }}
      onFinish={onFinish}
      layout="vertical"
      requiredMark={false}
    >
      <PrimaryTitle $isMobile={isMobile}>
        Welcome to Cornix! <br /> Login to your Account
      </PrimaryTitle>
      <Form.Item label="E-mail" name="email" rules={EMAIL_FORM_ITEM_RULES}>
        <Input size="large" />
      </Form.Item>
      <Form.Item
        label="Password"
        name="password"
        rules={[
          {
            required: true,
            message: "Please input your password",
          },
        ]}
        style={{ marginBottom: 8 }}
      >
        <Input.Password size="large" />
      </Form.Item>
      <RouterLink to="/reset-password" tabIndex={-1}>
        Forgot Password?
      </RouterLink>
      <AuthFormFooter
        buttonLoading={isLoading}
        buttonStyle={{ marginTop: 24 }}
      />
      <SocialLogins />
    </Form>
  );
};

export default LoginForm;
